(() => {
// -- Constants
const momaxHowItWorksTag = 'momax-how-it-works';

// -- Functions
class MomaxHowItWorks extends HTMLElement {
  rebuild() {
    const width = this.group.getBoundingClientRect().width;
    const viewportWidth = this.viewport.clientWidth;
    // ResizeObserver also fires after setup; do not restart an unchanged loop.
    if (this.animation && this.sequenceWidth === width && this.viewportWidth === viewportWidth && !this.motion.matches) return;
    const progress = this.animation && this.sequenceWidth
      ? (Number(this.animation.currentTime) / this.animation.effect.getTiming().duration) % 1 : 0;
    this.animation?.cancel();
    this.animation = null;
    this.ready = false;
    this.sequenceWidth = width;
    this.viewportWidth = viewportWidth;
    this.track.querySelectorAll('[data-hiw-clone]').forEach(node => node.remove());
    this.removeAttribute('data-running');
    const hasImages = [...this.group.children].some(node => node.getBoundingClientRect().width > 0);
    this.controls.hidden = !hasImages || this.motion.matches;
    if (!hasImages || !width || this.motion.matches) return;

    // Repeat the original sequence enough to cover wide viewports, even for one narrow image.
    const copies = Math.ceil(this.viewport.clientWidth / width);
    for (let index = 0; index < copies; index++) {
      const clone = this.group.cloneNode(true);
      clone.dataset.hiwClone = '';
      clone.setAttribute('aria-hidden', 'true');
      clone.inert = true;
      // Decorative copies must not inherit an original card's pending entrance.
      clone.querySelectorAll('[data-momax-reveal]').forEach(node => node.removeAttribute('data-momax-reveal'));
      clone.querySelectorAll('[data-shopify-editor-block]').forEach(node => node.removeAttribute('data-shopify-editor-block'));
      this.track.append(clone);
    }
    this.viewport.scrollLeft = 0;
    this.setAttribute('data-running', '');
    this.animation = this.track.animate([
      { transform: 'translateX(0)' }, { transform: `translateX(-${width}px)` }
    ], { duration: width / Number(this.dataset.speed) * 1000, iterations: Infinity, easing: 'linear' });
    this.animation.currentTime = progress * this.animation.effect.getTiming().duration;
    this.updatePlayback();
    const animation = this.animation;
    // Transformed offscreen copies must be decoded before they enter the viewport.
    const images = [...this.track.querySelectorAll('img')].filter(image => image.closest('picture').getBoundingClientRect().width > 0);
    Promise.all(images.map(image => {
      image.loading = 'eager';
      return image.decode().catch(() => {});
    })).then(() => {
      if (!this.isConnected || this.animation !== animation) return;
      this.ready = true;
      this.updatePlayback();
    });
  }

  updatePlayback() {
    const paused = !this.ready || this.paused || this.editing || !this.visible || document.hidden;
    if (paused) this.animation?.pause();
    else this.animation?.play();
    this.button.textContent = this.paused ? this.dataset.resume : this.dataset.pause;
    this.button.setAttribute('aria-pressed', String(this.paused));
  }

  // -- Lifecycle hooks
  connectedCallback() {
    this.viewport = this.querySelector('[data-hiw-viewport]');
    this.track = this.querySelector('[data-hiw-track]');
    this.group = this.querySelector('[data-hiw-group]');
    this.controls = this.querySelector('[data-hiw-controls]');
    this.button = this.querySelector('button');
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.paused = false;
    this.visible = true;
    this.events = new AbortController();
    const options = { signal:this.events.signal };
    this.button.addEventListener('click', () => { this.paused = !this.paused; this.updatePlayback(); }, options);
    this.motion.addEventListener('change', () => this.rebuild(), options);
    document.addEventListener('visibilitychange', () => this.updatePlayback(), options);
    this.addEventListener('shopify:block:select', () => { this.editing = true; this.updatePlayback(); }, options);
    this.addEventListener('shopify:block:deselect', () => { this.editing = false; this.updatePlayback(); }, options);
    this.resize = new ResizeObserver(() => this.rebuild());
    this.resize.observe(this.viewport);
    this.resize.observe(this.group);
    this.intersection = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; this.updatePlayback(); });
    this.intersection.observe(this);
    this.rebuild();
  }

  disconnectedCallback() {
    this.animation?.cancel();
    this.animation = null;
    this.resize?.disconnect();
    this.intersection?.disconnect();
    this.events?.abort();
  }
}

// -- Lifecycle hooks
if (!customElements.get(momaxHowItWorksTag)) customElements.define(momaxHowItWorksTag, MomaxHowItWorks);

})();
